import { cn } from '@/lib/utils'

/**
 * Get the base input classes shared by ALL input types
 * This includes layout, borders, hover, focus, and disabled states
 */
export const getBaseInputClasses = (
  disabled?: boolean,
  isBorderless?: boolean,
  readOnly?: boolean,
) => {
  return cn(
    // Core layout and spacing
    'w-full px-3 py-2 rounded text-sm border border-gray-4 bg-white focus:outline-none',
    isBorderless && 'border-none',
    // Text colors
    'placeholder:text-gray-8 data-[placeholder]:text-gray-8 text-gray-10',
    // Interactive states
    !disabled &&
      !isBorderless &&
      !readOnly &&
      'hover:border-gray-6 focus:border-blue-8 focus:shadow-input-focus',
    // Disabled state
    disabled && 'bg-gray-1 text-gray-9 border-gray-5',
    'disabled:bg-gray-1 disabled:text-gray-9 disabled:border-gray-5',
  )
}

/**
 * Get classes for compound input containers (wrapper div around input + adornments)
 */
export const getInputContainerClasses = (
  disabled?: boolean,
  error?: boolean,
  isBorderless?: boolean,
  readOnly?: boolean,
) => {
  return cn(
    // Container-specific layout
    'group flex min-w-0 items-center rounded border border-gray-4 text-sm overflow-hidden bg-white focus-within:outline-none',
    isBorderless && 'border-none',
    // Focus-within states (for containers)
    !disabled &&
      !readOnly &&
      !isBorderless &&
      'hover:border-gray-6 focus-within:hover:border-blue-8 focus-within:border-blue-8 focus-within:shadow-input-focus',
    // Disabled state
    disabled && 'bg-gray-1 text-gray-9 border-gray-5',
    // Error state
    error && !isBorderless && 'border-red-5 focus-within:border-red-8',
  )
}

/**
 * Get classes for bare input fields inside containers
 */
export const getInputFieldClasses = (type?: string, className?: string) => {
  return cn(
    // Base field classes (no border since container handles it)
    'group w-full min-w-0 bg-transparent border-none px-2.5 py-2 text-sm outline-none placeholder:text-gray-8',
    // Type-specific webkit overrides
    type === 'number' && 'hide-spinner',
    '[&::-webkit-inner-spin-button]:hidden [&::-webkit-inner-spin-button]:-webkit-appearance-none',
    type === 'date' && 'pe-1',
    '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-datetime-edit-month-field]:uppercase [&::-webkit-datetime-edit-day-field]:uppercase [&::-webkit-datetime-edit-year-field]:uppercase [&::-webkit-calendar-picker]:hidden [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:-webkit-appearance-none',
    className,
  )
}

/**
 * Get classes for input adornments (start/end icons or content)
 */
export const getInputAdornmentClasses = (
  position: 'start' | 'end',
  disabled?: boolean,
  endAdornmentSpacing?: string,
  startAdornmentSpacing?: string,
) => {
  return cn(
    'flex items-center text-gray-10',
    position === 'start' && startAdornmentSpacing,
    position === 'end' && endAdornmentSpacing,
    disabled && 'text-gray-9',
  )
}
